// @flow

import React from "react";
import { localize } from "redux-i18n";
import actionCreators from "@modules/client/actionCreator";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Checkbox } from "@components";
import type { EmployeeType } from "@modules/employee/types";
import type { MiLogs } from "@modules/reporting/reducer/reports/mi-reports/types";
import ShareForm from "./share-form";
import SecurityWarning from "./security-warning";

type OwnProps = $Exact<{
  clientId: number,
  toggleShareModal: () => any,
  onEmployeesSearch: Object => any,
  onSharePresentation: Object => any,
  clearEmployees: () => any,
  modalData: Object,
  employees: EmployeeType[],
  currentUserId: number | null,
  getLogs: Object => any,
  logs: MiLogs[],
}>;

type OwnState = $Exact<{
  selected: number[],
  searchValue: string,
  isConfirmed: boolean,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
}>;

export class MiShareModal extends React.Component<Props, OwnState> {
  state = {
    selected: [],
    searchValue: "",
    isConfirmed: false,
  };

  componentDidMount() {
    const { getLogs, modalData, clientId } = this.props;

    if (clientId && modalData.report && modalData.report.status !== "NOT_SHARED") {
      getLogs({
        clientId,
        report: modalData.report,
      });
    }
  }

  componentWillUnmount() {
    this.props.clearEmployees();
  }

  onSelect = (employeeId: number) => () =>
    this.setState({
      selected: this.state.selected.includes(employeeId)
        ? this.state.selected.filter(id => id != employeeId)
        : [...this.state.selected, employeeId],
    });

  handleChange = (e: SyntheticEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    this.setState({ searchValue: value });
  };

  handleSearch = (e: *) => {
    if (e.key === "Enter" || this.state.searchValue.length > 1) {
      this.props.onEmployeesSearch({
        search: this.state.searchValue,
        clientId: this.props.clientId,
      });
      this.setState({ selected: [] });
    }
  };

  sharePresentation = () => {
    this.props.onSharePresentation({
      report: this.props.modalData.report,
      employees: this.selectedEmployees,
      clientId: this.props.clientId,
    });
    this.props.toggleShareModal();
  };

  confirmNotice = () => {
    this.setState({ isConfirmed: true });
  };

  isValid = () => this.state.selected.length > 0;

  get selectedEmployees() {
    const { selected } = this.state;
    return this.props.employees.filter(el => selected.includes(el.id));
  }

  get columns() {
    return [
      {
        key: "employeeRefPrimary",
        width: 150,
        align: "left",
        align: "left",
        name: this.props.t("EMPLOYEE_REF"),
      },
      {
        key: "signature",
        align: "left",
        width: 150,
        name: this.props.t("EMPLOYEE_NAME"),
        renderCell: ({ row }: Object) => (
          <div className="ellipsis">
            {row.firstName} {row.lastName}
          </div>
        ),
      },
      {
        key: "department",
        width: 150,
        name: this.props.t("DEPARTMENT"),
        align: "left",
        renderCell: ({ row }: Object) => {
          let res;
          if (row.employmentSummary.organisationalUnits.length) {
            res = row.employmentSummary.organisationalUnits.join(", ");
          } else {
            res = this.props.t("N/A");
          }
          return <div className="ellipsis">{res}</div>;
        },
      },
      {
        key: "email",
        align: "left",
        name: this.props.t("PRIMARY_EMAIL_ADDRESS"),
        renderCell: ({ row }: Object) => (
          <div className="ellipsis">{(row.contactDetails || {}).workEmail || row.workEmail}</div>
        ),
      },
      {
        key: "presentationId",
        width: 100,
        name: this.props.t("PRESENTATION_ID"),
        renderCell: ({ row }: Object) =>
          this.alreadySharedWith && this.alreadySharedWith.includes(row.id)
            ? `${this.props.modalData.report.id} ${this.props.t("ALREADY_SHARED")}`
            : this.props.modalData.report.id,
        align: "center",
      },
      {
        key: "selected",
        width: 50,
        name: this.props.t("SELECT"),
        renderCell: ({ row }: Object) => (
          <Checkbox
            checked={this.state.selected.includes(row.id)}
            id={`mi-publish-employee-${row.id}`}
            onChange={this.onSelect(row.id)}
            disabled={this.alreadySharedWith && this.alreadySharedWith.includes(row.id)}
            className="px-0"
          />
        ),
        align: "center",
      },
    ];
  }

  get employees() {
    const { currentUserId } = this.props;
    return this.props.employees.filter(employee => employee.id !== currentUserId);
  }

  get alreadySharedWith() {
    const { logs } = this.props;
    if (logs && logs.length) {
      return logs.map(el => el.sharedWith.id);
    }

    return null;
  }

  render() {
    return (
      <Modal className="min-modal-width">
        <ModalHeader
          toggle={this.props.toggleShareModal}
          className="d-flex justify-content-between"
        >
          {this.props.t(this.state.isConfirmed ? "SHARE_PRESENTATION" : "MI_PRESENTATIONS")}
        </ModalHeader>
        <ModalBody className="limited-height">
          {this.state.isConfirmed ? (
            <ShareForm
              handleChange={this.handleChange}
              searchValue={this.state.searchValue}
              handleSearch={this.handleSearch}
              cols={this.columns}
              employees={this.employees}
            />
          ) : (
            <SecurityWarning />
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.props.toggleShareModal}>
            {this.props.t("CANCEL")}
          </Button>
          {this.state.isConfirmed ? (
            <Button disabled={!this.isValid()} onClick={this.sharePresentation}>
              {this.props.t("SHARE")}
            </Button>
          ) : (
            <Button onClick={this.confirmNotice}>{this.props.t("PROCEED_TO_SHARE")}</Button>
          )}
        </ModalFooter>
      </Modal>
    );
  }
}

export default localize()(MiShareModal);
